import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from 'uuid';
import {
    Button,
    Input,
    Container,
    Box,
    SpaceBetween,
    Table,
    Modal,
    Select,
    Tabs,
    FormField,
    Checkbox,
    Multiselect
} from "@amzn/awsui-components-react";

import { createAuditLog } from "../Templates/Utils/AppActivityUtilsAppsyncPlus";
import { createPreference, deletePreference } from "src/utilities/AppSyncPlusAPIs";
import { BANNER_ASSOCIATION_ID_TYPES, BANNER_DISPLAY_EVERYWHERE_ID, PULSE_SURVEY_NOTIFICATION_PREFERENCE_TYPE, GROUP_PULSE_SURVEY_NOTIFICATION_PREFERENCE_TYPE, GROUP_PULSE_SURVEY_ASSOCIATION_ID, GROUP_PULSE_SURVEY_ASSOCIATION_ID_TYPE } from "src/constants/AppConstants";
import { getOwnersForAdminUser } from "src/utilities/CloseAPIs";
import { getSingleSurveyNotifications, getGroupSurveyNotifications } from "./PulseSurveyNotificationUtils";
import PulseSurveyNotification from "./PulseSurveyNotification";
import { formatTimestamp, getCurrentTime, isLongerArray } from "../utils/generalUtils";
import BannerCustomMultiInput from "../Banners/BannerCustomMultiInput";
import { BannerInfoMessage } from "../Banners/BannerInfoMessage";
import { ALL, pulseSurveyTabIds, DisplayOptions, displayOptions, triggerDisplayOption, notificationDisplayTypes, ActionTriggerOptions, actionTriggerOptions, AutoPopupDelayTypes, autoPopupDelayTypeOptions } from "./PulseSurveyNotificationConstants";
import Banner from "../Banners/Banner";

export default (props) => {
    const [notificationText, setNotificationText] = useState("");
    const [notificationLabel, setNotificationLabel] = useState("");
    const [notificationDisplayType, setNotificationDisplayType] = useState(null);
    const [surveyId, setSurveyId] = useState("");
    const [selectedDisplay, setSelectedDisplay] = useState(null);
    const [dismissible, setDismissible] = useState(true);
    const [autoPopup, setAutoPopup] = useState(false);
    const [autoPopupMaxCount, setAutoPopupMaxCount] = useState("1");

    const [autoPopupDelayType, setAutoPopupDelayType] = useState(null);
    const [autoPopupDelaySeconds, setAutoPopupDelaySeconds] = useState("0");
    const [actionTrigger, setActionTrigger] = useState(null);

    const [closePortalOwners, setClosePortalOwners] = useState([]);
    const [urlPrefixes, setUrlPrefixes] = useState([]);
    const [bannerApps, setBannerApps] = useState([]);

    const [bannerAppOptions, setBannerAppOptions] = useState([]);
    const [closePortalOwnerOptions, setClosePortalOwnerOptions] = useState([]);
    const [currentSurveyNotifications, setCurrentSurveyNotifications] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [updatingSurveyNotification, setUpdatingSurveyNotification] = useState(null);
    const [showUpdateModal, setShowUpdateModal] = useState(false);

    const [activeTabId, setActiveTabId] = useState(pulseSurveyTabIds.CurrentSurveyNotifications);

    const getGroupDisplayLabel = (item) => {
        const info = item?.information;
        if (info) {
            switch (info.selectedDisplay?.value) {
                case DisplayOptions.CLOSE_PORTAL:
                    return `Close Portals: ${info.closePortalOwners.map(o => o.label).join(", ")}`;
                case DisplayOptions.URL_PREFIX:
                    return `Url Prefixes: ${info.urlPrefixes.map(u => u.label).join(", ")}`;
                case DisplayOptions.APP:
                    return `Apps: ${info.bannerApps.map(a => a.label).join(", ")}`;
            }
        }
        return "";
    };

    const getDisplayLabel = (item) => {
        const display = item.information.selectedDisplay?.value;
        if (item?.preferenceType === GROUP_PULSE_SURVEY_NOTIFICATION_PREFERENCE_TYPE) {
            return getGroupDisplayLabel(item);
        }
        switch (display) {
            case DisplayOptions.EVERYWHERE:
                return "Display Everywhere";
            case DisplayOptions.CLOSE_PORTAL:
                return `Close Portal: ${item.associationId === BANNER_DISPLAY_EVERYWHERE_ID ? "All Pages" : item?.information?.closePortalOwners[0]?.label}`;
            case DisplayOptions.URL_PREFIX:
                return `Url Prefix: ${item?.information?.urlPrefixes[0]?.label}`;
            case DisplayOptions.APP:
                return `App: ${item.associationId === BANNER_DISPLAY_EVERYWHERE_ID ? "All Pages" : item?.information?.bannerApps[0]?.label}`;
            case DisplayOptions.TRIGGER: {
                const triggerLabel = actionTriggerOptions.find(o => o.value === item?.information?.actionTrigger)?.label || item?.information?.actionTrigger;
                return `Trigger: ${triggerLabel}`;
            }
        }
        return "No Label";
    };

    const getUpdateButton = (item) => (
        <Button variant="inline-icon" iconName="refresh" onClick={() => openUpdateModal(item)} />
    );

    const getDeleteButton = (item) => (
        <Button variant="inline-icon" iconName="delete-marker" onClick={() => deleteSurveyNotificationAndAudit(item)} />
    );

    const tableColumnDefinitions = [
        { header: "ID", cell: item => item?.preferenceId },
        { header: "Notification Label", cell: item => item?.information?.notificationLabel },
        { header: "Notification Text", cell: item => item?.information?.notificationText },
        { header: "Type", cell: item => item?.information?.notificationDisplayType?.label },
        { header: "Survey Friendly ID", cell: item => item?.information?.surveyId || "—" },
        { header: "Display To", cell: getDisplayLabel },
        { header: "Dismissible", cell: item => item?.information?.dismissible?.toString() },
        { header: "Auto Pop-up", cell: item => item?.information?.autoPopup ? `true (max ${item?.information?.autoPopupMaxCount || '∞'})` : "false" },
        { header: "Created By", cell: item => item?.createdBy },
        { header: "Created At", cell: item => formatTimestamp(item?.createdTimestamp) },
        { header: "Update", cell: getUpdateButton },
        { header: "Delete", cell: getDeleteButton },
    ];

    const loadSurveyNotifications = async () => {
        const [surveyNotifications, groupSurveyNotifications] = await Promise.all([
            getSingleSurveyNotifications(), getGroupSurveyNotifications()
        ]);
        let groupedIds = new Set();
        groupSurveyNotifications.forEach(gs => {
            const ids = gs.information.notifications?.map(s => s.notificationId) || [];
            groupedIds = new Set([...ids, ...groupedIds]);
        });
        const ungrouped = surveyNotifications.filter(s => !groupedIds.has(s.preferenceId));
        setCurrentSurveyNotifications([...ungrouped, ...groupSurveyNotifications]);
    };

    const loadClosePortalOwnerOptions = async () => {
        await getOwnersForAdminUser((result) => {
            const data = result.data.getOwnersForAdminUserRDS;
            const owners = JSON.parse(data);
            setClosePortalOwnerOptions(owners.map(o => ({ label: o?.owner_name, value: String(o?.owner_id) })));
        }, (response) => {
            console.error(`Error loading Close Portal Owners: ${response}`);
        });
    };

    useEffect(() => {
        loadSurveyNotifications();
        loadClosePortalOwnerOptions();
    }, []);

    useEffect(() => {
        if (props?.allApps?.length > 0) {
            setBannerAppOptions(props.allApps.map(app => ({ label: app.app_name, value: app.app_id })));
        }
    }, [props.allApps]);

    const hasActionTriggerSet = actionTrigger && actionTrigger.value !== ActionTriggerOptions.NONE;

    const canCreateSurveyNotification = () => {
        const bannerFieldsValid = hasActionTriggerSet || (!!notificationText && !!notificationDisplayType);
        return !(
            !!notificationLabel && bannerFieldsValid && !!surveyId &&
            (
                selectedDisplay?.value === DisplayOptions.EVERYWHERE
                || selectedDisplay?.value === DisplayOptions.TRIGGER
                || closePortalOwners?.length > 0
                || urlPrefixes?.length > 0
                || bannerApps?.length > 0
            )
        );
    };

    const getSurveyInformation = () => ({
        notificationLabel,
        notificationText,
        notificationDisplayType,
        surveyId,
        selectedDisplay,
        closePortalOwners,
        urlPrefixes,
        bannerApps,
        dismissible,
        autoPopup,
        autoPopupMaxCount: autoPopup ? (isNaN(parseInt(autoPopupMaxCount)) ? 0 : parseInt(autoPopupMaxCount)) : undefined,
        autoPopupDelayType: autoPopup && autoPopupDelayType ? autoPopupDelayType.value : AutoPopupDelayTypes.NONE,
        autoPopupDelaySeconds: autoPopup && autoPopupDelayType && autoPopupDelayType.value !== AutoPopupDelayTypes.NONE ? (isNaN(parseInt(autoPopupDelaySeconds)) ? 0 : parseInt(autoPopupDelaySeconds)) : 0,
        actionTrigger: actionTrigger ? actionTrigger.value : ActionTriggerOptions.NONE,
    });

    const shouldCreateGroupSurvey = (info) => {
        if ([DisplayOptions.CLOSE_PORTAL, DisplayOptions.APP, DisplayOptions.URL_PREFIX].includes(info?.selectedDisplay?.value)) {
            if (isLongerArray(info.closePortalOwners) || isLongerArray(info.urlPrefixes) || isLongerArray(info.bannerApps)) {
                return true;
            }
        }
        return false;
    };

    const getAssociationIdAndType = (info, index = 0) => {
        let associationId, associationIdType;
        switch (info?.selectedDisplay?.value) {
            case DisplayOptions.EVERYWHERE:
                associationIdType = BANNER_ASSOCIATION_ID_TYPES.ABC;
                associationId = BANNER_DISPLAY_EVERYWHERE_ID;
                break;
            case DisplayOptions.CLOSE_PORTAL:
                associationIdType = BANNER_ASSOCIATION_ID_TYPES.CLOSE_PORTAL;
                associationId = info?.closePortalOwners === ALL ? BANNER_DISPLAY_EVERYWHERE_ID : info?.closePortalOwners[index]?.value;
                break;
            case DisplayOptions.URL_PREFIX:
                associationIdType = BANNER_ASSOCIATION_ID_TYPES.URL_PREFIX;
                associationId = info?.urlPrefixes[index]?.label?.toLowerCase();
                break;
            case DisplayOptions.APP:
                associationIdType = BANNER_ASSOCIATION_ID_TYPES.APP;
                associationId = info?.bannerApps === ALL ? BANNER_DISPLAY_EVERYWHERE_ID : info?.bannerApps[index]?.value;
                break;
        }
        return [associationId, associationIdType];
    };

    const createSurveyNotification = async (surveyNotificationInfo) => {
        setSuccessMessage("");
        setErrorMessage("");

        if (shouldCreateGroupSurvey(surveyNotificationInfo)) {
            return createGroupSurveyNotification(surveyNotificationInfo);
        }

        let [associationId, associationIdType] = getAssociationIdAndType(surveyNotificationInfo);

        return await createSingleSurveyNotification(
            PULSE_SURVEY_NOTIFICATION_PREFERENCE_TYPE, associationId, associationIdType, props.userId, surveyNotificationInfo
        ).then((preferenceId) => {
            setSuccessMessage(`Pulse Survey Notification created successfully with ID: ${preferenceId}`);
            resetForm();
            loadSurveyNotifications();
        }, (error) => {
            setErrorMessage(`Error creating pulse survey notification: ${error}`);
        });
    };

    const createSingleSurveyNotification = async (prefType, associationId, associationIdType, userId, info) => {
        const newId = uuidv4();
        return new Promise((resolve, reject) => {
            createPreference(newId, prefType, info.notificationLabel, associationId, associationIdType, userId, info, null,
                () => resolve(newId),
                (err) => reject(err)
            );
        });
    };

    const createGroupSurveyNotification = async (surveyNotificationInfo) => {
        let singleItems;
        switch (surveyNotificationInfo.selectedDisplay?.value) {
            case DisplayOptions.CLOSE_PORTAL: singleItems = surveyNotificationInfo.closePortalOwners; break;
            case DisplayOptions.URL_PREFIX: singleItems = surveyNotificationInfo.urlPrefixes; break;
            case DisplayOptions.APP: singleItems = surveyNotificationInfo.bannerApps; break;
        }

        const newSurveyNotifications = (await Promise.all(singleItems.map((_, index) => {
            let [associationId, associationIdType] = getAssociationIdAndType(surveyNotificationInfo, index);
            return createSingleSurveyNotification(PULSE_SURVEY_NOTIFICATION_PREFERENCE_TYPE, associationId, associationIdType, props.userId, surveyNotificationInfo);
        }))).flat();

        const groupInfo = newSurveyNotifications.map((id, index) => ({ notificationId: id, ...singleItems[index] }));

        await createSingleSurveyNotification(
            GROUP_PULSE_SURVEY_NOTIFICATION_PREFERENCE_TYPE, GROUP_PULSE_SURVEY_ASSOCIATION_ID, GROUP_PULSE_SURVEY_ASSOCIATION_ID_TYPE, props.userId,
            { ...surveyNotificationInfo, notifications: groupInfo }
        ).then((preferenceId) => {
            setSuccessMessage(`Group Pulse Survey created with ID: ${preferenceId}`);
            resetForm();
            loadSurveyNotifications();
        });
    };

    const createSurveyNotificationAndAudit = async (info) => {
        await createSurveyNotification(info);
        createAuditLog(
            `${PULSE_SURVEY_NOTIFICATION_PREFERENCE_TYPE}#`, `${getCurrentTime()}`,
            "Creating a pulse survey", JSON.stringify({}), JSON.stringify(info),
            "CREATE PULSE_SURVEY", props.userId,
            () => {}, () => {}
        );
    };

    const resetForm = () => {
        setNotificationText("");
        setNotificationLabel("");
        setNotificationDisplayType(null);
        setSurveyId("");
        setDismissible(true);
        setAutoPopup(false);
        setAutoPopupMaxCount("1");
        setAutoPopupDelayType(null);
        setAutoPopupDelaySeconds("0");
        setActionTrigger(null);
        setSelectedDisplay(null);
        setClosePortalOwners([]);
        setUrlPrefixes([]);
        setBannerApps([]);
    };

    const openUpdateModal = (survey) => {
        setUpdatingSurveyNotification(survey);
        const info = survey?.information;
        setNotificationText(info?.notificationText);
        setNotificationLabel(survey?.label || info?.notificationLabel);
        setNotificationDisplayType(info?.notificationDisplayType);
        setSurveyId(info?.surveyId || "");
        setDismissible(info?.dismissible);
        setAutoPopup(info?.autoPopup || false);
        setAutoPopupMaxCount(String(info?.autoPopupMaxCount ?? 1));
        setAutoPopupDelayType(info?.autoPopupDelayType ? autoPopupDelayTypeOptions.find(o => o.value === info.autoPopupDelayType) || null : null);
        setAutoPopupDelaySeconds(String(info?.autoPopupDelaySeconds ?? 0));
        setActionTrigger(info?.actionTrigger ? actionTriggerOptions.find(o => o.value === info.actionTrigger) || null : null);
        setSelectedDisplay(info?.selectedDisplay);
        setClosePortalOwners(info?.closePortalOwners || []);
        setUrlPrefixes(info?.urlPrefixes || []);
        setBannerApps(info?.bannerApps || []);
        setShowUpdateModal(true);
    };

    const closeUpdateModal = () => {
        resetForm();
        setShowUpdateModal(false);
    };

    const updateSurveyNotification = async () => {
        setSuccessMessage("");
        setErrorMessage("");
        const info = getSurveyInformation();
        try {
            await createSurveyNotification(info);
            await deleteSurveyNotification(updatingSurveyNotification);
            closeUpdateModal();
            createAuditLog(
                `${PULSE_SURVEY_NOTIFICATION_PREFERENCE_TYPE}#`, `${getCurrentTime()}`,
                "Updating a pulse survey notification", JSON.stringify(updatingSurveyNotification.information), JSON.stringify(info),
                "ALTER PULSE_SURVEY", props.userId, () => {}, () => {}
            );
        } catch (error) {
            setErrorMessage(`Update failed: ${error}. Original notification was not removed.`);
        }
    };

    const deleteSurveyNotificationPromise = (id) => {
        return new Promise((resolve, reject) => {
            deletePreference(id, null, () => resolve(id), (err) => reject(err));
        });
    };

    const deleteSurveyNotification = async (item) => {
        if (item.preferenceType === GROUP_PULSE_SURVEY_NOTIFICATION_PREFERENCE_TYPE) {
            const ids = item.information.notifications?.map(s => s.notificationId) || [];
            await Promise.all([...ids.map(id => deleteSurveyNotificationPromise(id)), deleteSurveyNotificationPromise(item.preferenceId)]);
        } else {
            await deleteSurveyNotificationPromise(item.preferenceId);
        }
        loadSurveyNotifications();
    };

    const deleteSurveyNotificationAndAudit = async (item) => {
        await deleteSurveyNotification(item);
        createAuditLog(
            `${PULSE_SURVEY_NOTIFICATION_PREFERENCE_TYPE}#`, `${getCurrentTime()}`,
            "Deleting a pulse survey", JSON.stringify(item.information), JSON.stringify({}),
            "DELETE PULSE_SURVEY", props.userId, () => {}, () => {}
        );
    };

    const getSurveyNotificationForm = () => (
        <Box margin="m">
            <SpaceBetween size="m">
                <FormField label="Notification Label">
                    <Input value={notificationLabel} onChange={({ detail }) => setNotificationLabel(detail.value)} placeholder="Internal label for this notification..." />
                </FormField>

                <FormField label="Notification Text">
                    <Input value={notificationText} onChange={({ detail }) => setNotificationText(detail.value)} placeholder="Type your notification message here..." disabled={hasActionTriggerSet} />
                </FormField>

                <FormField label="Notification Display Type">
                    <Select options={notificationDisplayTypes} selectedOption={notificationDisplayType} onChange={({ detail }) => setNotificationDisplayType(detail.selectedOption)} placeholder="Select notification display type" disabled={hasActionTriggerSet} />
                </FormField>

                <FormField label="Survey Friendly ID" description="The ID passed to the Pulse Widget to open when interacting with the notification banner">
                    <Input value={surveyId} onChange={({ detail }) => setSurveyId(detail.value)} placeholder="Enter survey friendly ID..." />
                </FormField>

                <FormField label="Dismissible">
                    <Checkbox checked={dismissible} onChange={() => setDismissible(!dismissible)} />
                </FormField>

                <FormField label="Auto Pop-up" description="When enabled, the survey will automatically pop up for users without requiring interaction">
                    <Checkbox checked={autoPopup} onChange={() => setAutoPopup(!autoPopup)} />
                </FormField>

                {autoPopup && (
                    <FormField label="Max Pop-up Count" description="Number of times the survey will auto pop-up per user. Set to 0 or leave empty for unlimited pop-ups.">
                        <Input type="number" value={autoPopupMaxCount} onChange={({ detail }) => setAutoPopupMaxCount(detail.value)} placeholder="0 for unlimited" />
                    </FormField>
                )}

                {autoPopup && (
                    <FormField label="Pop-up Delay" description="Choose when the auto pop-up should appear after the page loads">
                        <Select
                            options={hasActionTriggerSet
                                ? autoPopupDelayTypeOptions.filter(o => o.value !== AutoPopupDelayTypes.SESSION_TIME)
                                : autoPopupDelayTypeOptions
                            }
                            selectedOption={autoPopupDelayType}
                            onChange={({ detail }) => {
                                const selected = detail.selectedOption;
                                setAutoPopupDelayType(selected);
                                // Auto-set display location to "Everywhere" when session time is selected
                                if (selected?.value === AutoPopupDelayTypes.SESSION_TIME) {
                                    const everywhereOption = displayOptions.find(o => o.value === DisplayOptions.EVERYWHERE);
                                    setSelectedDisplay(everywhereOption);
                                } else if (selectedDisplay?.value === DisplayOptions.EVERYWHERE && autoPopupDelayType?.value === AutoPopupDelayTypes.SESSION_TIME) {
                                    // Clear display location if switching away from session time and it was auto-set
                                    setSelectedDisplay(null);
                                }
                            }}
                            placeholder="Select delay type"
                        />
                        {hasActionTriggerSet && (
                            <BannerInfoMessage>"Session Time" delay is not available when an action trigger is set. "Time on Page" delay can still be used.</BannerInfoMessage>
                        )}
                    </FormField>
                )}

                {autoPopup && autoPopupDelayType && autoPopupDelayType.value !== AutoPopupDelayTypes.NONE && (
                    <FormField label="Delay (seconds)" description="Number of seconds to wait before showing the pop-up">
                        <Input type="number" value={autoPopupDelaySeconds} onChange={({ detail }) => setAutoPopupDelaySeconds(detail.value)} placeholder="Enter delay in seconds" />
                    </FormField>
                )}

                {autoPopup && (
                    <FormField label="Action Trigger" description="Pop up the survey upon a specific user action. When set, the display location is automatically set to 'Trigger'.">
                        <Select
                            options={actionTriggerOptions}
                            selectedOption={actionTrigger}
                            disabled={autoPopupDelayType?.value === AutoPopupDelayTypes.SESSION_TIME}
                            onChange={({ detail }) => {
                                const selected = detail.selectedOption;
                                setActionTrigger(selected);
                                // Auto-set display location to "trigger" when an action trigger is selected
                                if (selected && selected.value !== ActionTriggerOptions.NONE) {
                                    setSelectedDisplay(triggerDisplayOption);
                                } else if (selectedDisplay?.value === DisplayOptions.TRIGGER) {
                                    setSelectedDisplay(null);
                                }
                            }}
                            placeholder="Select action trigger"
                        />
                        {autoPopupDelayType?.value === AutoPopupDelayTypes.SESSION_TIME && (
                            <BannerInfoMessage>Action trigger is not available when "Session Time" delay is selected. They cannot be used together.</BannerInfoMessage>
                        )}
                    </FormField>
                )}

                <FormField label="Where Will The Survey Be Displayed">
                    <Select
                        options={displayOptions}
                        selectedOption={selectedDisplay}
                        onChange={({ detail }) => setSelectedDisplay(detail.selectedOption?.label === selectedDisplay?.label ? null : detail.selectedOption)}
                        disabled={hasActionTriggerSet || (autoPopupDelayType?.value === AutoPopupDelayTypes.SESSION_TIME)}
                    />
                    {hasActionTriggerSet && (
                        <BannerInfoMessage>Display location is automatically set to "Trigger" when an action trigger is selected. Remove the action trigger to change this.</BannerInfoMessage>
                    )}
                    {autoPopupDelayType?.value === AutoPopupDelayTypes.SESSION_TIME && !hasActionTriggerSet && (
                        <BannerInfoMessage>Display location is set to "Everywhere" when session time delay is selected, since the survey needs to be visible across all pages.</BannerInfoMessage>
                    )}
                </FormField>

                {selectedDisplay?.value === DisplayOptions.CLOSE_PORTAL && (
                    <FormField label="Which Close Portal(s)">
                        <SpaceBetween size="m">
                            <Checkbox checked={closePortalOwners === ALL} onChange={() => setClosePortalOwners(closePortalOwners === ALL ? [] : ALL)}>All Close Portal Pages</Checkbox>
                            <Multiselect options={closePortalOwnerOptions} selectedOptions={closePortalOwners === ALL ? [] : closePortalOwners} filteringType="auto" onChange={({ detail }) => setClosePortalOwners(detail.selectedOptions)} disabled={closePortalOwners === ALL} />
                        </SpaceBetween>
                    </FormField>
                )}

                {selectedDisplay?.value === DisplayOptions.URL_PREFIX && (
                    <FormField label="Which Url Prefix(es)">
                        <BannerInfoMessage>Urls must be in form of {"/<url-you-want>"}. example: /firstpass</BannerInfoMessage>
                        <BannerCustomMultiInput updateInputs={(value) => setUrlPrefixes(value)} getInputs={() => urlPrefixes} />
                    </FormField>
                )}

                {selectedDisplay?.value === DisplayOptions.APP && (
                    <FormField label="Which App(s)">
                        <SpaceBetween size="m">
                            <Checkbox checked={bannerApps === ALL} onChange={() => setBannerApps(bannerApps === ALL ? [] : ALL)}>All App Pages</Checkbox>
                            <Multiselect options={bannerAppOptions} selectedOptions={bannerApps === ALL ? [] : bannerApps} filteringType="auto" onChange={({ detail }) => setBannerApps(detail.selectedOptions)} disabled={bannerApps === ALL} />
                        </SpaceBetween>
                    </FormField>
                )}
            </SpaceBetween>
        </Box>
    );

    const getSurveyNotificationPreview = () => (
        <Box>
            <SpaceBetween size="s">
                <Box fontWeight="bold">Survey Notification Preview</Box>
                <Box>
                    {hasActionTriggerSet ? (
                        <BannerInfoMessage>No banner will be shown for this notification. The survey will open automatically when the selected action is performed.</BannerInfoMessage>
                    ) : notificationText && notificationDisplayType ? (
                        <div className="pulse-survey-preview">
                            <PulseSurveyNotification notificationText={notificationText} notificationDisplayType={notificationDisplayType.value} surveyId={surveyId} options={{ roundedCorners: true, onDismiss: dismissible ? () => {} : undefined }} />
                        </div>
                    ) : (
                        <BannerInfoMessage>Set Notification Text and Notification Display Type to preview</BannerInfoMessage>
                    )}
                </Box>
            </SpaceBetween>
        </Box>
    );

    return (
        <Container>
            <h3>Pulse Survey Notifications</h3>

            <Tabs
                activeTabId={activeTabId}
                onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
                tabs={[
                    {
                        label: "Current Survey Notifications",
                        id: pulseSurveyTabIds.CurrentSurveyNotifications,
                        content: (
                            <Box>
                                <Table columnDefinitions={tableColumnDefinitions} items={currentSurveyNotifications} stickyHeader={true} resizableColumns={true} />
                            </Box>
                        )
                    },
                    {
                        label: "Create Survey Notification",
                        id: pulseSurveyTabIds.CreateSurveyNotification,
                        content: (
                            <Box>
                                <SpaceBetween size="m">
                                    <Box>
                                        {successMessage && (
                                            <Banner bannerMessage={successMessage} bannerType="success" bannerKey="0" options={{ onDismiss: () => setSuccessMessage(""), roundedCorners: true }} />
                                        )}
                                        {errorMessage && (
                                            <Banner bannerMessage={errorMessage} bannerType="error" bannerKey="0" options={{ onDismiss: () => setErrorMessage(""), roundedCorners: true }} />
                                        )}
                                    </Box>
                                    {getSurveyNotificationPreview()}
                                    <Box>
                                        <Button variant="primary" disabled={canCreateSurveyNotification()} onClick={() => createSurveyNotificationAndAudit(getSurveyInformation())}>Create Survey Notification</Button>
                                    </Box>
                                </SpaceBetween>
                                {getSurveyNotificationForm()}
                            </Box>
                        )
                    }
                ]}
            />

            <Modal
                onDismiss={() => closeUpdateModal()}
                visible={showUpdateModal}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => closeUpdateModal()}>Cancel</Button>
                            <Button disabled={canCreateSurveyNotification()} variant="primary" onClick={() => updateSurveyNotification()}>Update</Button>
                        </SpaceBetween>
                    </Box>
                }
                header="Update Pulse Survey"
            >
                <SpaceBetween size="m">
                    {getSurveyNotificationPreview()}
                    {getSurveyNotificationForm()}
                </SpaceBetween>
            </Modal>
        </Container>
    );
};
